import React, { useState, useEffect } from 'react';
import {
  Row,
  Col,
  Card,
  CardHeader,
  CardBody,
  Table,
  Badge,
  Button,
  Input,
  FormGroup,
  Label,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Alert,
  Spinner,
  Collapse,
  InputGroup,
  InputGroupAddon,
  InputGroupText
} from 'reactstrap';
import { useAuth } from '../../../contexts/AuthContext';
import dealService from '../../../service/dealService.ts';
import documentsApi from '../../../service/documentsApi';
import DocumentPreview from '../../../components/Documents/DocumentPreview';
import DocumentDownloadButton from '../../../components/Documents/DocumentDownloadButton';
import { usePaymentValidation, FIELD_CONSTRAINTS } from './hooks/usePaymentValidation';

const CollectionsTab = ({ deal }) => {
  const { getToken, user } = useAuth();

  // State for balance calculation (needed before validation hook)
  const [remainingBalance, setRemainingBalance] = useState(0);
  const [totalPaid, setTotalPaid] = useState(0);
  const [netPayback, setNetPayback] = useState(0);
  const [overdueBalance, setOverdueBalance] = useState(0); // 🆕 Balance vencido

  // Payment validation hook
  const { validationErrors, validateForm, clearValidationErrors, clearFieldError } = usePaymentValidation(remainingBalance);

  // State for payment form
  const [paymentForm, setPaymentForm] = useState({
    paymentDate: new Date().toISOString().split('T')[0],
    reference: '',
    comment: '',
    amount: '',
    attachment: null
  });

  // State for payments list (paginated)
  const [payments, setPayments] = useState([]);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [paymentsError, setPaymentsError] = useState(null);
  
  // State for installments (facturas con asignación de pagos)
  const [installments, setInstallments] = useState([]);

  // State for form submission
  const [savingPayment, setSavingPayment] = useState(false);
  const [savePaymentError, setSavePaymentError] = useState(null);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  // Pagination state for payments
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize] = useState(12); // Changed to 12 as requested
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);

  // State for accordion
  const [accordionOpen, setAccordionOpen] = useState({
    addPayment: true,
    paymentsList: true,
    installmentsPlan: true
  });

  // State for file selection error
  const [uploadError, setUploadError] = useState(null);

  // Deal status
  const [dealFinished, setDealFinished] = useState(false);

  // State for document preview modal
  const [previewModal, setPreviewModal] = useState({
    isOpen: false,
    documentId: null
  });

  // Toggle accordion sections
  const toggleAccordion = (section) => {
    setAccordionOpen(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Open preview modal
  const openPreviewModal = (documentId) => {
    setPreviewModal({
      isOpen: true,
      documentId: documentId
    });
  };

  // Close preview modal
  const closePreviewModal = () => {
    setPreviewModal({
      isOpen: false,
      documentId: null
    });
  };

  // Format currency
  const formatNumber = (amount) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const [year, month, day] = dateString.split('-');
      const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  // Get installment status (badge label and color)
  const getInstallmentStatus = (installment) => {
    if (installment.isPaid) {
      return { label: '✓ Paid', color: 'success' };
    } else if (installment.isOverdue) {
      return { label: '⚠ Overdue', color: 'danger' };
    } else if (installment.amountPaid > 0) {
      return { label: '◐ Partial', color: 'warning' };
    } else {
      return { label: '○ Pending', color: 'secondary' };
    }
  };

  // Get progress percentage for an installment
  const getProgressPercentage = (installment) => {
    if (!installment.amount || installment.amount === 0) return 0;
    return (installment.amountPaid / installment.amount) * 100;
  };

  // Get progress bar color for an installment
  const getProgressColor = (installment) => {
    if (installment.isPaid) return 'success';
    if (installment.isOverdue) return 'danger';
    if (installment.amountPaid > 0) return 'warning';
    return 'secondary';
  };

  // Calculate overall progress
  const getOverallProgress = () => {
    if (!netPayback || netPayback === 0) return 0;
    return (totalPaid / netPayback) * 100;
  };

  // Load paginated payments using the collections endpoint
  const loadPayments = async (page = 0) => {
    if (!deal || !deal.privateId) {
      console.warn('No deal or privateId available');
      return;
    }

    try {
      setPaymentsLoading(true);
      setPaymentsError(null);

      const token = getToken();
      if (!token) {
        throw new Error('No authentication token available');
      }

      console.log('Loading paginated payments for deal:', deal.privateId, 'page:', page);
      
      // Get paginated payments from API
      const paymentsResponse = await dealService.listPayments(
        token, 
        deal.privateId, 
        page, 
        pageSize, 
        'paymentDate,desc'
      );
      
      const paymentsData = paymentsResponse.data;
      console.log('Paginated payments received:', paymentsData);

      // Update state with paginated payments data
      setPayments(paymentsData.content || []);
      setCurrentPage(paymentsData.number || 0);
      setTotalPages(paymentsData.totalPages || 0);
      setTotalElements(paymentsData.totalElements || 0);

    } catch (error) {
      console.error('Error loading paginated payments:', error);
      
      // Handle 404 - no payments data yet
      if (error.response?.status === 404) {
        console.log('No payments data found yet - initializing empty state');
        setPayments([]);
        setCurrentPage(0);
        setTotalPages(0);
        setTotalElements(0);
      } else {
        setPaymentsError(error.message || 'Failed to load payments data');
      }
    } finally {
      setPaymentsLoading(false);
    }
  };

  // Load deal summary data (balances, installments) using real API
  const loadDealSummaryData = async () => {
    if (!deal || !deal.privateId) {
      console.warn('No deal or privateId available');
      return;
    }

    try {
      const token = getToken();
      if (!token) {
        throw new Error('No authentication token available');
      }

      console.log('Loading collections summary for deal:', deal.privateId);
      
      // Get collections summary from API
      const summaryResponse = await dealService.getCollectionsSummary(token, deal.privateId);
      const summary = summaryResponse.data;

      console.log('Collections summary received:', summary);

      // Update state with summary data (excluding payments)
      setNetPayback(summary.netPayback || 0);
      setTotalPaid(summary.totalPaid || 0);
      setRemainingBalance(summary.remainingBalance || 0);
      setOverdueBalance(summary.overdueBalance || 0);
      setInstallments(summary.installments || []);
      setDealFinished(summary.dealStatus === 'FINISHED');

      // Return the summary for use by calling function
      return summary;

    } catch (error) {
      console.error('Error loading summary data:', error);
      
      // Handle 404 - no collections data yet
      if (error.response?.status === 404) {
        console.log('No collections data found yet - initializing empty state');
        setInstallments([]);
        setNetPayback(0);
        setTotalPaid(0);
        setRemainingBalance(0);
        setOverdueBalance(0);
        setDealFinished(false);
      }
      
      return null;
    }
  };

  // Load all deal data (summary + paginated payments)
  const loadDealPaymentData = async () => {
    // Load summary data and paginated payments in parallel
    await Promise.all([
      loadDealSummaryData(),
      loadPayments(0) // Load first page of payments
    ]);
  };

  // Generate dynamic voucher filename
  const generateVoucherFileName = (reference, publicDealId, originalFile) => {
    // Get current date in format YYYYMMDD
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const dateStr = `${year}${month}${day}`;
    
    // Clean reference (remove special characters and spaces)
    const cleanReference = reference.trim().replace(/[^a-zA-Z0-9]/g, '_');
    
    // Get file extension from original file
    const originalName = originalFile.name;
    const lastDotIndex = originalName.lastIndexOf('.');
    const extension = lastDotIndex > 0 ? originalName.substring(lastDotIndex) : '';
    
    // Format: reference_date_publicDealId.extension
    const newFileName = `${cleanReference}_${dateStr}_${publicDealId}${extension}`;
    
    console.log('Generated voucher filename:', newFileName);
    return newFileName;
  };

  // Handle file selection (store file for later upload with payment)
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    console.log('File selected:', file.name);
    
    // Generate dynamic filename immediately when file is selected
    const newFileName = generateVoucherFileName(
      paymentForm.reference || 'NOREF',
      deal.publicId,
      file
    );
    
    // Create a new File object with the new name
    const renamedFile = new File(
      [file], 
      newFileName, 
      { type: file.type }
    );
    
    console.log('File renamed to:', newFileName);
    
    // Store the renamed File object in the payment form
    setPaymentForm(prev => ({ 
      ...prev, 
      attachment: renamedFile
    }));

    setUploadError(null);
  };

  // Handle payment form submission using real API
  const handleSavePayment = async () => {
    if (!deal || !deal.privateId) {
      console.error('Deal privateId is required to save payment');
      return;
    }

    // Validate form using hook
    const isValid = validateForm(paymentForm);
    if (!isValid) {
      // Show first validation error
      const firstError = Object.values(validationErrors)[0];
      setSavePaymentError(firstError || 'Please fix validation errors');
      return;
    }

    try {
      setSavingPayment(true);
      setSavePaymentError(null);

      const token = getToken();
      if (!token) {
        throw new Error('No authentication token available');
      }

      // Step 1: Upload voucher document first if attachment exists
      let uploadedDocumentId = null;
      if (paymentForm.attachment instanceof File) {
        try {
          console.log('Uploading payment voucher...', {
            fileName: paymentForm.attachment.name,
            fileSize: paymentForm.attachment.size,
            publicId: deal.publicId
          });
          
          const uploadResult = await documentsApi.uploadDocument({
            file: paymentForm.attachment, // File is already renamed in handleFileSelect
            category: 'deal',
            originType: 'paymentVoucher',
            originId: deal.publicId, // Use publicId instead of privateId
            description: 'payment voucher copy',
            tags: 'payment voucher'
          });
          
          console.log('Upload result:', uploadResult);
          uploadedDocumentId = uploadResult.documentId;
          console.log('Payment voucher uploaded successfully. Document ID:', uploadedDocumentId);
          
          if (!uploadedDocumentId) {
            console.error('Upload succeeded but no documentId returned:', uploadResult);
            throw new Error('Document uploaded but no ID was returned. Please try again.');
          }
        } catch (uploadError) {
          console.error('Error uploading payment voucher:', uploadError);
          throw new Error('Failed to upload payment voucher. Please try again.');
        }
      } else {
        console.log('No file attachment to upload');
      }

      // Step 2: Prepare payment payload (only fields expected by backend)
      const paymentPayload = {
        paymentDate: paymentForm.paymentDate,
        reference: paymentForm.reference.trim(),
        amount: parseFloat(paymentForm.amount)
      };

      // Add optional fields only if they have values
      if (paymentForm.comment && paymentForm.comment.trim()) {
        paymentPayload.comment = paymentForm.comment.trim();
      }

      // Add documentId if voucher was uploaded
      console.log('Checking uploadedDocumentId before adding to payload:', uploadedDocumentId);
      if (uploadedDocumentId) {
        paymentPayload.documentId = uploadedDocumentId;
        console.log('DocumentId added to payload:', paymentPayload.documentId);
      } else {
        console.log('No documentId to add to payload');
      }

      console.log('Final payment payload to be sent:', JSON.stringify(paymentPayload, null, 2));

      // Step 3: Register payment with documentId using real API (no file attachment)
      const response = await dealService.registerPayment(
        token, 
        deal.privateId, 
        paymentPayload
        // Note: No file parameter since we upload voucher separately
      );

      console.log('Payment registered successfully:', response.data);

      // Reload summary data and payments to get updated data
      await Promise.all([
        loadDealSummaryData(),
        loadPayments(currentPage) // Reload current page of payments
      ]);

      // Update remaining amount in deal after payment registration
      try {
        // Use the current remainingBalance from state (updated by loadDealSummaryData)
        if (remainingBalance !== undefined && remainingBalance !== null) {
          await dealService.updateRemainingAmount(token, deal.privateId, remainingBalance);
          console.log('Deal remaining amount updated successfully:', remainingBalance);
        }
      } catch (updateError) {
        console.error('Error updating deal remaining amount:', updateError);
        // Don't throw error - payment was successful, this is just a sync update
      }

      // Reset form
      setPaymentForm({
        paymentDate: new Date().toISOString().split('T')[0],
        reference: '',
        comment: '',
        amount: '',
        attachment: null
      });

      // Clear validation errors
      clearValidationErrors();

      // Clear file input
      const fileInput = document.getElementById('paymentProof');
      if (fileInput) fileInput.value = '';

      // Show success message
      if (response.data.dealStatus === 'FINISHED') {
        setToastMessage('🎉 Payment registered! Deal is now FINISHED - All payments collected!');
      } else {
        setToastMessage('Payment registered successfully!');
      }
      setShowToast(true);
      setTimeout(() => setShowToast(false), 5000);

    } catch (error) {
      console.error('Error saving payment:', error);
      
      // Handle validation errors from backend
      if (error.response?.status === 422) {
        const detail = error.response.data?.detail || 'Validation error';
        setSavePaymentError(detail);
      } else if (error.response?.status === 404) {
        setSavePaymentError('Deal not found. Please check the deal ID.');
      } else if (error.response?.status === 403) {
        setSavePaymentError('You do not have permission to add payments to this deal.');
      } else {
        setSavePaymentError(error.message || 'Failed to save payment');
      }
    } finally {
      setSavingPayment(false);
    }
  };

  // Load payment data when component mounts
  useEffect(() => {
    loadDealPaymentData();
  }, [deal]);

  return (
    <div>
      {/* Toast notification */}
      {showToast && (
        <Alert color="success" className="position-fixed" style={{ top: '20px', right: '20px', zIndex: 9999 }}>
          <i className="ni ni-check-bold mr-2" />
          {toastMessage}
        </Alert>
      )}

      <Row>
        <Col>
          {/* Balance Summary Card */}
          <Card className="shadow-sm mb-4">
            <CardHeader className="bg-info text-white">
              <div className="d-flex justify-content-between align-items-center">
                <div className="d-flex align-items-center">
                  <i className="ni ni-money-coins mr-2" />
                  <h5 className="mb-0">Payment Summary</h5>
                </div>
                {dealFinished && (
                  <Badge color="success" className="p-2">
                    <i className="ni ni-check-bold mr-1" />
                    Paid off
                  </Badge>
                )}
              </div>
            </CardHeader>
            <CardBody>
              {/* Congratulations message when deal is finished */}
              {dealFinished && (
                <Alert color="success" className="mb-3">
                  <div className="text-center">
                    <h4 className="alert-heading">
                      <i className="ni ni-trophy mr-2" />
                      Congratulations!
                    </h4>
                    <p className="mb-0">
                      All payments have been collected. This deal is now finished.
                    </p>
                  </div>
                </Alert>
              )}

              <Row>
                <Col lg="3" className="text-center mb-3">
                  <div className="text-muted small font-weight-bold">NET PAYBACK</div>
                  <div className="h4 font-weight-bold text-black">
                    {formatNumber(netPayback)}
                  </div>
                </Col>
                <Col lg="3" className="text-center mb-3">
                  <div className="text-muted small font-weight-bold">TOTAL PAID</div>
                  <div className="h4 font-weight-bold text-black">
                    {formatNumber(totalPaid)}
                  </div>
                </Col>
                <Col lg="3" className="text-center mb-3">
                  <div className="text-muted small font-weight-bold">REMAINING BALANCE</div>
                  <div className={`h4 font-weight-bold ${remainingBalance > 0 ? 'text-warning' : 'text-success'}`}>
                    {formatNumber(remainingBalance)}
                  </div>
                </Col>
                <Col lg="3" className="text-center mb-3">
                  <div className="text-muted small font-weight-bold">OVERDUE BALANCE</div>
                  <div className={`h4 font-weight-bold ${overdueBalance > 0 ? 'text-danger' : 'text-red'}`}>
                    {overdueBalance > 0 && <i className="ni ni-fat-remove mr-1" />}
                    {formatNumber(overdueBalance)}
                  </div>
                  {overdueBalance > 0 && (
                    <small className="text-danger d-block">
                      <i className="ni ni-bell-55 mr-1" />
                      Requires attention
                    </small>
                  )}
                </Col>
              </Row>
            </CardBody>
          </Card>


          {/* Payment Plan and Payment History Side by Side */}
          <Row>
            {/* Payment Plan (Installments) Table - Left Side */}
            <Col lg="6" className="pr-2">
              {installments.length > 0 && (
                <Card className="shadow-sm mb-4">
                  <CardHeader 
                    className="d-flex justify-content-between align-items-center cursor-pointer"
                    onClick={() => toggleAccordion('installmentsPlan')}
                    style={{ cursor: 'pointer', backgroundColor: accordionOpen.installmentsPlan ? '#f8f9fa' : '#fff' }}
                  >
                    <div className="d-flex align-items-center">
                      <i className="ni ni-bullet-list-67 text-primary mr-2" />
                      <h6 className="mb-0 text-muted font-weight-bold">PAYMENT PLAN</h6>
                      <Badge color="light" className="ml-2 text-dark">
                        {installments.length} installments
                      </Badge>
                    </div>
                    <Button 
                      color="link" 
                      size="sm" 
                      className="p-0"
                    >
                      <i className={`ni ${accordionOpen.installmentsPlan ? 'ni-fat-remove' : 'ni-fat-add'}`} 
                        style={{ fontSize: '1.0rem' }}
                      />
                    </Button>
                  </CardHeader>
                  <Collapse isOpen={accordionOpen.installmentsPlan !== false}>
                    <CardBody>
                      <div className="table-responsive">
                        <Table className="table-flush" responsive style={{ fontSize: '0.7rem', tableLayout: 'fixed' }}>
                          <thead className="thead-light">
                            <tr>
                              <th scope="col" style={{ textAlign: 'center', width: '30px', padding: '0.5rem 0.25rem' }}>#</th>
                              <th scope="col" style={{ width: '75px', padding: '0.5rem 0.25rem' }}>Due Date</th>
                              <th scope="col" style={{ textAlign: 'right', width: '60px', padding: '0.5rem 0.25rem' }}>Amount</th>
                              <th scope="col" style={{ textAlign: 'right', width: '60px', padding: '0.5rem 0.25rem' }}>Paid</th>
                              <th scope="col" style={{ textAlign: 'right', width: '60px', padding: '0.5rem 0.25rem' }}>Remaining</th>
                              <th scope="col" style={{ textAlign: 'center', width: '90px', padding: '0.5rem 0.25rem' }}>Payments</th>
                              <th scope="col" style={{ textAlign: 'center', width: '60px', padding: '0.5rem 0.25rem' }}>Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {installments.map((installment) => {
                              const status = getInstallmentStatus(installment);
                              const progress = getProgressPercentage(installment);
                              const progressColor = getProgressColor(installment);

                              return (
                                <tr 
                                  key={installment.id}
                                  className={installment.isOverdue ? 'table-danger' : ''}
                                >
                                  <td style={{ textAlign: 'center', padding: '0.5rem 0.25rem' }}>
                                    <Badge color="light" className="badge-circle" style={{ fontSize: '0.6rem', padding: '0.2em 0.4em' }}>
                                      <strong>{installment.number}</strong>
                                    </Badge>
                                  </td>
                                  <td style={{ padding: '0.5rem 0.25rem' }}>
                                    <span className="text-sm" style={{ fontSize: '0.65rem' }}>
                                      {formatDate(installment.date)}
                                    </span>
                                  </td>
                                  <td style={{ textAlign: 'right', padding: '0.5rem 0.25rem' }}>
                                    <span className="font-weight-bold" style={{ fontSize: '0.65rem' }}>
                                      {formatNumber(installment.amount)}
                                    </span>
                                  </td>
                                  <td style={{ textAlign: 'right', padding: '0.5rem 0.25rem' }}>
                                    <span className="font-weight-bold text-success" style={{ fontSize: '0.65rem' }}>
                                      {formatNumber(installment.amountPaid)}
                                    </span>
                                  </td>
                                  <td style={{ textAlign: 'right', padding: '0.5rem 0.25rem' }}>
                                    <span className={`font-weight-bold ${installment.isPaid ? 'text-muted' : 'text-warning'}`} style={{ fontSize: '0.65rem' }}>
                                      {formatNumber(installment.remainingAmount)}
                                    </span>
                                  </td>
                                  <td style={{ textAlign: 'center', verticalAlign: 'middle', padding: '0.5rem 0.25rem' }}>
                                    {installment.paymentReferences && installment.paymentReferences.length > 0 ? (
                                      <div>
                                        {installment.paymentReferences.map((ref, index) => (
                                          <div key={index} style={{ textAlign: 'center', marginBottom: '1px', fontSize: '0.6rem' }}>
                                            {ref}
                                          </div>
                                        ))}
                                      </div>
                                    ) : (
                                      <span className="text-muted" style={{ fontSize: '0.65rem' }}>-</span>
                                    )}
                                  </td>
                                  <td style={{ textAlign: 'center', padding: '0.5rem 0.25rem' }}>
                                    <Badge color={status.color} className="badge-sm" style={{ fontSize: '0.6rem', padding: '0.2em 0.4em' }}>
                                      {status.label}
                                    </Badge>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </Table>
                      </div>
                    </CardBody>
                  </Collapse>
                </Card>
              )}
            </Col>

            {/* Payment History Table - Right Side */}
            <Col lg="6" className="pl-2">
              <Card className="shadow-sm mb-4">
                <CardHeader 
                  className="d-flex justify-content-between align-items-center cursor-pointer"
                  onClick={() => toggleAccordion('paymentsList')}
                  style={{ cursor: 'pointer', backgroundColor: accordionOpen.paymentsList ? '#f8f9fa' : '#fff' }}
                >
                  <div className="d-flex align-items-center">
                    <i className="ni ni-calendar-grid-58 text-primary mr-2" />
                    <h6 className="mb-0 text-muted font-weight-bold">PAYMENTS HISTORY</h6>
                    {payments.length > 0 && (
                      <Badge color="light" className="ml-2 text-dark">
                        {payments.length} payments
                      </Badge>
                    )}
                  </div>
                  <Button 
                    color="link" 
                    size="sm" 
                    className="p-0"
                  >
                    <i className={`ni ${accordionOpen.paymentsList ? 'ni-fat-remove' : 'ni-fat-add'}`} 
                      style={{ fontSize: '1.0rem' }}
                    />
                  </Button>
                </CardHeader>
                <Collapse isOpen={accordionOpen.paymentsList}>
                  <CardBody>
                    {/* Loading State */}
                    {paymentsLoading && (
                      <div className="text-center py-4">
                        <Spinner color="primary" />
                        <p className="mt-2 mb-0 text-muted">Loading payments...</p>
                      </div>
                    )}

                    {/* Error State */}
                    {paymentsError && !paymentsLoading && (
                      <Alert color="danger" className="mb-3">
                        <i className="ni ni-support-16 mr-2" />
                        {paymentsError}
                        <Button 
                          color="outline-danger" 
                          size="sm" 
                          className="ml-2"
                          onClick={loadDealPaymentData}
                        >
                          Retry
                        </Button>
                      </Alert>
                    )}

                    {/* Payments Table */}
                    {!paymentsLoading && !paymentsError && (
                      <div className="table-responsive">
                        <Table className="align-items-center table-flush" responsive style={{ fontSize: '0.8rem', tableLayout: 'fixed' }}>
                          <thead className="thead-light">
                            <tr>
                              <th scope="col" style={{ width: '80px', padding: '0.5rem 0.25rem' }}>Date</th>
                              <th scope="col" style={{ width: '100px', padding: '0.5rem 0.25rem' }}>Reference</th>
                              <th scope="col" style={{ textAlign: 'right', width: '80px', padding: '0.5rem 0.25rem' }}>Amount</th>
                              <th scope="col" style={{ textAlign: 'center', width: '80px', padding: '0.5rem 0.25rem' }}>Voucher</th>
                              <th scope="col" style={{ width: '80px', padding: '0.5rem 0.25rem' }}>User</th>
                            </tr>
                          </thead>
                          <tbody>
                            {payments.length === 0 ? (
                              <tr>
                                <td colSpan="5" className="text-center text-muted py-4">
                                  No payments found
                                </td>
                              </tr>
                            ) : (
                              payments.map((payment) => (
                                <tr key={payment.id}>
                                  <td style={{ padding: '0.5rem 0.25rem' }}>
                                    <span className="text-sm font-weight-bold" style={{ fontSize: '0.7rem' }}>
                                      {formatDate(payment.paymentDate)}
                                    </span>
                                  </td>
                                  <td style={{ padding: '0.5rem 0.25rem' }}>
                                    <span className="font-weight-bold text-dark" style={{ fontSize: '0.7rem' }}>
                                      {payment.reference}
                                    </span>
                                  </td>
                                  <td style={{ textAlign: 'right', padding: '0.5rem 0.25rem' }}>
                                    <span className="font-weight-bold text-success" style={{ fontSize: '0.7rem' }}>
                                      {formatNumber(payment.amount)}
                                    </span>
                                  </td>
                                  <td style={{ textAlign: 'center', padding: '0.5rem 0.25rem' }}>
                                    {payment.documentId ? (
                                      <div className="d-flex align-items-center justify-content-center">
                                        <DocumentDownloadButton
                                          documentId={payment.documentId}
                                          label={<i className="ni ni-cloud-download-95" style={{ fontSize: '0.8rem' }} />}
                                          className="btn-outline-primary btn-sm mr-1"
                                          style={{ padding: '0.2rem 0.4rem' }}
                                        />
                                        <Button
                                          color="secondary"
                                          size="sm"
                                          onClick={() => openPreviewModal(payment.documentId)}
                                          title="Preview document"
                                          style={{ padding: '0.2rem 0.4rem' }}
                                        >
                                          <i className="ni ni-album-2" style={{ fontSize: '0.8rem' }} />
                                        </Button>
                                      </div>
                                    ) : (
                                      <span className="text-muted" style={{ fontSize: '0.7rem' }}>-</span>
                                    )}
                                  </td>
                                  <td style={{ padding: '0.5rem 0.25rem' }}>
                                    <span className="text-sm text-muted" style={{ fontSize: '0.7rem' }}>
                                      {payment.createdBy}
                                    </span>
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </Table>
                      </div>
                    )}

                    {/* Pagination */}
                    {!paymentsLoading && !paymentsError && totalElements > 0 && (
                      <div className="d-flex justify-content-between align-items-center mt-3">
                        <small className="text-muted">
                          Showing {payments.length} of {totalElements} payments
                        </small>
                        <div className="d-flex align-items-center">
                          <Button
                            color="outline-secondary"
                            size="sm"
                            disabled={currentPage === 0}
                            onClick={() => loadPayments(currentPage - 1)}
                            className="mr-2"
                            title="Previous page"
                          >
                            <i className="ni ni-bold-left" />
                          </Button>
                          <span className="mx-2">
                            Page {currentPage + 1} of {totalPages || 1}
                          </span>
                          <Button
                            color="outline-secondary"
                            size="sm"
                            disabled={currentPage >= (totalPages - 1)}
                            onClick={() => loadPayments(currentPage + 1)}
                            title="Next page"
                          >
                            <i className="ni ni-bold-right" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </CardBody>
                </Collapse>
              </Card>
            </Col>
          </Row>

          {/* Add Payment Form */}
          <Card className="shadow-sm mb-4">
            <CardHeader 
              className="d-flex justify-content-between align-items-center cursor-pointer"
              onClick={() => toggleAccordion('addPayment')}
              style={{ cursor: 'pointer', backgroundColor: accordionOpen.addPayment ? '#f8f9fa' : '#fff' }}
            >
              <div className="d-flex align-items-center">
                <i className="ni ni-fat-add text-primary mr-2" />
                <h6 className="mb-0 text-muted font-weight-bold">ADD PAYMENT</h6>
              </div>
              <Button 
                color="link" 
                size="sm" 
                className="p-0"
              >
                <i className={`ni ${accordionOpen.addPayment ? 'ni-fat-remove' : 'ni-fat-add'}`} 
                  style={{ fontSize: '1.0rem' }}
                />
              </Button>
            </CardHeader>
            <Collapse isOpen={accordionOpen.addPayment}>
              <CardBody>
                {dealFinished && (
                  <Alert color="info" className="mb-3">
                    <i className="ni ni-lock-circle-open mr-2" />
                    This deal is finished. No further payments are allowed.
                  </Alert>
                )}

                {savePaymentError && (
                  <Alert color="danger" className="mb-3">
                    <i className="ni ni-support-16 mr-2" />
                    {savePaymentError}
                  </Alert>
                )}

                <Row>
                  <Col lg="6" className="mb-1">
                    <FormGroup>
                      <Label className="form-control-label text-muted small font-weight-bold">
                        PAYMENT DATE *
                      </Label>
                      <Input
                        type="date"
                        value={paymentForm.paymentDate}
                        onChange={(e) => setPaymentForm(prev => ({ ...prev, paymentDate: e.target.value }))}
                        disabled={dealFinished}
                        aria-label="Payment date"
                      />
                    </FormGroup>
                  </Col>
                  <Col lg="6" className="mb-1">
                    <FormGroup>
                      <Label className="form-control-label text-muted small font-weight-bold">
                        AMOUNT *
                      </Label>
                      <Input
                        type="number"
                        value={paymentForm.amount}
                        onChange={(e) => {
                          const value = e.target.value;
                          // Prevent negative values
                          if (value && parseFloat(value) < 0) return;
                          
                          // Prevent exceeding remaining balance (with 1% tolerance)
                          if (value && remainingBalance !== undefined && remainingBalance !== null) {
                            const numValue = parseFloat(value);
                            const tolerance = remainingBalance * 0.01;
                            if (numValue > (remainingBalance + tolerance)) {
                              // Don't update if it exceeds the limit
                              return;
                            }
                          }
                          
                          setPaymentForm(prev => ({ ...prev, amount: value }));
                          clearFieldError('amount');
                        }}
                        onWheel={(e) => {
                          // Prevent scroll from changing the value
                          e.target.blur();
                        }}
                        placeholder="Enter amount"
                        disabled={dealFinished}
                        aria-label="Payment amount"
                        min="0"
                        max={remainingBalance}
                        invalid={!!validationErrors.amount}
                      />
                      {validationErrors.amount && (
                        <small className="text-danger d-block mt-1">
                          {validationErrors.amount}
                        </small>
                      )}
                      <small className="text-muted">
                        Remaining balance: {formatNumber(remainingBalance)}
                      </small>
                    </FormGroup>
                  </Col>
                </Row>

                <Row>
                  <Col lg="6" className="mb-3">
                    <FormGroup>
                      <Label className="form-control-label text-muted small font-weight-bold">
                        COMMENT
                      </Label>
                      <Input
                        type="textarea"
                        rows="3"
                        value={paymentForm.comment}
                        onChange={(e) => {
                          const value = e.target.value;
                          // Only allow letters, numbers, spaces, and basic punctuation
                          const sanitizedValue = value.replace(/[^a-zA-Z0-9\s\-_\.\,\!\?]/g, '');
                          // Enforce max length
                          if (sanitizedValue.length <= FIELD_CONSTRAINTS.comment.maxLength) {
                            setPaymentForm(prev => ({ ...prev, comment: sanitizedValue }));
                            clearFieldError('comment');
                          }
                        }}
                        onBlur={(e) => {
                          // Trim leading/trailing whitespace on blur
                          const trimmedValue = e.target.value.replace(/^\s+|\s+$/g, '');
                          setPaymentForm(prev => ({ ...prev, comment: trimmedValue }));
                        }}
                        placeholder="Enter comment (optional)"
                        disabled={dealFinished}
                        aria-label="Payment comment"
                        maxLength={FIELD_CONSTRAINTS.comment.maxLength}
                        invalid={!!validationErrors.comment}
                      />
                      <small className="text-muted">
                        {paymentForm.comment.length}/{FIELD_CONSTRAINTS.comment.maxLength} characters
                      </small>
                      {validationErrors.comment && (
                        <small className="text-danger d-block mt-1">
                          {validationErrors.comment}
                        </small>
                      )}
                    </FormGroup>
                  </Col>
                  <Col lg="6" className="mb-3">
                    <FormGroup>
                      <Label className="form-control-label text-muted small font-weight-bold">
                        REFERENCE *
                      </Label>
                      <Input
                        type="text"
                        value={paymentForm.reference}
                        onChange={(e) => {
                          const value = e.target.value;
                          // Only allow letters, numbers, and basic punctuation
                          const sanitizedValue = value.replace(/[^a-zA-Z0-9\-_\.]/g, '');
                          // Enforce max length
                          if (sanitizedValue.length <= FIELD_CONSTRAINTS.reference.maxLength) {
                            setPaymentForm(prev => ({ ...prev, reference: sanitizedValue }));
                            clearFieldError('reference');
                          }
                        }}
                        onBlur={(e) => {
                          // Trim whitespace on blur
                          const trimmedValue = e.target.value.trim();
                          setPaymentForm(prev => ({ ...prev, reference: trimmedValue }));
                        }}
                        placeholder="Enter reference"
                        disabled={dealFinished}
                        aria-label="Payment reference"
                        maxLength={FIELD_CONSTRAINTS.reference.maxLength}
                        invalid={!!validationErrors.reference}
                      />
                      <small className="text-muted">
                        {paymentForm.reference.length}/{FIELD_CONSTRAINTS.reference.maxLength} characters
                      </small>
                      {validationErrors.reference && (
                        <small className="text-danger d-block mt-1">
                          {validationErrors.reference}
                        </small>
                      )}
                    </FormGroup>
                  </Col>
                </Row>

                <Row>
                  <Col lg="6" className="mb-3">
                    <FormGroup>
                      <Label className="form-control-label text-muted small font-weight-bold">
                        PAYMENT PROOF
                      </Label>
                      {uploadError && (
                        <Alert color="danger" className="mb-2">
                          <i className="ni ni-support-16 mr-2" />
                          {uploadError}
                        </Alert>
                      )}
                      {!dealFinished && (
                        <div className="mb-2">
                          <input
                            type="file"
                            id="paymentProof"
                            onChange={handleFileSelect}
                            style={{ display: 'none' }}
                            aria-label="Select payment proof"
                            accept=".pdf,.png,.jpg,.jpeg,.gif,.doc,.docx,.xls,.xlsx,.txt,.csv"
                          />
                          <Button
                            color="primary"
                            size="sm"
                            onClick={() => document.getElementById('paymentProof').click()}
                          >
                            <i className="ni ni-cloud-upload-96 mr-1" />
                            {paymentForm.attachment ? 'Change File' : 'Select File'}
                          </Button>
                        </div>
                      )}
                      {paymentForm.attachment && (
                        <div className="p-2 border rounded bg-white">
                          <small className="text-success d-block">
                            <i className="ni ni-check-bold mr-1" />
                            File selected: {paymentForm.attachment.name}
                          </small>
                          <small className="text-muted d-block mt-1">
                            File will be uploaded when you save the payment
                          </small>
                        </div>
                      )}
                    </FormGroup>
                  </Col>
                </Row>

                <div className="text-right">
                  <Button
                    color="primary"
                    onClick={handleSavePayment}
                    disabled={savingPayment || dealFinished}
                  >
                    {savingPayment ? (
                      <>
                        <Spinner size="sm" className="mr-1" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <i className="ni ni-check-bold mr-1" />
                        Save Payment
                      </>
                    )}
                  </Button>
                </div>
              </CardBody>
            </Collapse>
          </Card>

        </Col>
      </Row>

      {/* Document Preview Modal */}
      {previewModal.isOpen && previewModal.documentId && (
        <DocumentPreview
          documentId={previewModal.documentId}
          variant="modal"
          isOpen={previewModal.isOpen}
          onClose={closePreviewModal}
        />
      )}
    </div>
  );
};

export default CollectionsTab;
